import { useMemo, useState } from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import type { Position, Portfolio } from "@/types/portfolio"
import { isEffectiveDripEnabled } from "@/lib/portfolio-utils"
import { useCurrency } from "@/context/currency/currency-context.ts"
import { cn, formatShares } from "@/lib/utils"
import { ArrowUpDown, ArrowUp, ArrowDown, RefreshCcw, AlertTriangle } from "lucide-react"
import { SafetyBadge } from "@/components/custom/dashboard/safety-badge"

type SortColumn =
	| "ticker"
	| "shares"
	| "price"
	| "value"
	| "return"
	| "yield"
	| "yoc"
	| "growth"
	| "income"
	| "nextDiv"
	| "safety"
type SortDirection = "asc" | "desc"

function getReturnPct(pos: Position): number {
	const avgPrice = pos.average_price || 0
	const price = pos.price || 0
	if (avgPrice <= 0) return 0
	return ((price - avgPrice) / avgPrice) * 100
}

function getUnrealizedGain(pos: Position): number {
	const costBasis = pos.cost_basis || (pos.average_price || 0) * (pos.shares || 0)
	const currentValue = (pos.shares || 0) * (pos.price || 0)
	return currentValue - costBasis
}

function getSortValue(
	pos: Position,
	column: SortColumn,
	convert: (amount: number, fromCurrency?: string) => number,
): number | string {
	switch (column) {
		case "ticker":
			return pos.ticker
		case "shares":
			return pos.shares || 0
		case "price":
			return convert(pos.price || 0, pos.currency)
		case "value":
			return convert((pos.shares || 0) * (pos.price || 0), pos.currency)
		case "return":
			return pos.total_return_pct != null ? pos.total_return_pct : getReturnPct(pos)
		case "yield":
			return pos.dividendYield || 0
		case "yoc":
			return pos.yield_on_cost || 0
		case "growth":
			return pos.dividendGrowth5y || 0
		case "income":
			return convert((pos.shares || 0) * (pos.dividendPerShare || 0), pos.currency)
		case "nextDiv":
			return pos.next_dividend_date ? new Date(pos.next_dividend_date).getTime() : 0
		case "safety":
			return pos.safety_score ?? -1
	}
}

interface PositionsTableProps {
	positions: Position[]
	onRowClick?: (position: Position) => void
	portfolio?: Portfolio
}

export function PositionsTable({ positions, onRowClick, portfolio }: PositionsTableProps) {
	const { convert, localFormat } = useCurrency()
	const [sortColumn, setSortColumn] = useState<SortColumn>("value")
	const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

	const handleSort = (column: SortColumn) => {
		if (sortColumn === column) {
			setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
		} else {
			setSortColumn(column)
			setSortDirection("desc")
		}
	}

	const SortIcon = ({ column }: { column: SortColumn }) => {
		if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />
		return sortDirection === "asc" ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />
	}

	const sortedPositions = useMemo(() => {
		return [...positions].sort((a, b) => {
			const valA = getSortValue(a, sortColumn, convert)
			const valB = getSortValue(b, sortColumn, convert)
			const dir = sortDirection === "asc" ? 1 : -1
			if (typeof valA === "string" && typeof valB === "string") {
				return valA.localeCompare(valB) * dir
			}
			return ((valA as number) - (valB as number)) * dir
		})
	}, [positions, sortColumn, sortDirection, convert])

	const headCls = "text-xs font-medium uppercase tracking-wide text-muted-foreground select-none whitespace-nowrap"
	const sortHeadCls = `${headCls} cursor-pointer hover:text-foreground transition-colors`

	const stickyTickerHead = cn(
		sortHeadCls,
		"sticky left-0 z-20 bg-white dark:bg-[hsl(213,60%,8%)] border-r border-sky-900/10 dark:border-sky-100/10 pl-3 sm:pl-6",
	)
	const stickyTickerCell =
		"sticky left-0 z-10 bg-white dark:bg-[hsl(213,60%,8%)] border-r border-sky-900/10 dark:border-sky-100/10 group-hover:bg-sky-50 dark:group-hover:bg-[hsl(213,50%,12%)] pl-3 sm:pl-6 py-3.5 font-semibold text-sm text-sky-600 dark:text-sky-400 whitespace-nowrap transition-colors"

	return (
		<div className="w-full min-w-0 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10 overflow-hidden">
			<div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-sky-900/10 dark:border-sky-100/10">
				<h2 className="font-semibold text-base text-sky-950 dark:text-sky-50">Current Holdings</h2>
			</div>
			<div className="overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent border-sky-900/5 dark:border-sky-100/5">
							<TableHead className={stickyTickerHead} onClick={() => handleSort("ticker")}>
								<span className="inline-flex items-center gap-1">
									Ticker
									<SortIcon column="ticker" />
								</span>
							</TableHead>
							<TableHead className={headCls}>Company</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("shares")}>
								<span className="inline-flex items-center gap-1">
									Shares
									<SortIcon column="shares" />
								</span>
							</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("price")}>
								<span className="inline-flex items-center gap-1">
									Price
									<SortIcon column="price" />
								</span>
							</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("value")}>
								<span className="inline-flex items-center gap-1">
									Value
									<SortIcon column="value" />
								</span>
							</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("return")}>
								<span className="inline-flex items-center gap-1">
									Return
									<SortIcon column="return" />
								</span>
							</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("yield")}>
								<span className="inline-flex items-center gap-1">
									Yield
									<SortIcon column="yield" />
								</span>
							</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("yoc")}>
								<span className="inline-flex items-center gap-1">
									YoC
									<SortIcon column="yoc" />
								</span>
							</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("nextDiv")}>
								<span className="inline-flex items-center gap-1">
									Next Div
									<SortIcon column="nextDiv" />
								</span>
							</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("safety")}>
								<span className="inline-flex items-center gap-1">
									Safety
									<SortIcon column="safety" />
								</span>
							</TableHead>
							<TableHead className={sortHeadCls} onClick={() => handleSort("growth")}>
								<span className="inline-flex items-center gap-1">
									5Y Growth
									<SortIcon column="growth" />
								</span>
							</TableHead>
							<TableHead className={headCls}>Freq</TableHead>
							<TableHead className={`${sortHeadCls} pr-3 sm:pr-6`} onClick={() => handleSort("income")}>
								<span className="inline-flex items-center gap-1">
									Est. Income
									<SortIcon column="income" />
								</span>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{sortedPositions.length === 0 ? (
							<TableRow>
								<TableCell colSpan={13} className="h-24 text-center text-sm text-muted-foreground">
									No positions found. Add some honey to your hive!
								</TableCell>
							</TableRow>
						) : (
							sortedPositions.map((pos) => {
								const returnPct = getReturnPct(pos)
								const displayReturnPct = pos.total_return_pct != null ? pos.total_return_pct : returnPct
								const growth = pos.dividendGrowth5y || 0
								return (
									<TableRow
										key={pos.ticker}
										className={cn(
											"group border-sky-900/5 dark:border-sky-100/5 transition-colors",
											onRowClick && "cursor-pointer hover:bg-sky-50 dark:hover:bg-[hsl(213,50%,12%)]",
										)}
										onClick={() => onRowClick?.(pos)}
									>
										<TableCell className={stickyTickerCell}>
											<span className="inline-flex items-center gap-1">
												{pos.ticker}
												{(pos.effective_drip_enabled ??
													isEffectiveDripEnabled(pos, portfolio)) && (
													<Tooltip>
														<TooltipTrigger asChild>
															<RefreshCcw className="h-3 w-3 text-amber-500" />
														</TooltipTrigger>
														<TooltipContent>DRIP enabled</TooltipContent>
													</Tooltip>
												)}
											</span>
										</TableCell>
										<TableCell className="py-3.5 text-sm text-muted-foreground max-w-[160px] truncate">
											{pos.name}
										</TableCell>

										<TableCell className="py-3.5 text-sm text-foreground whitespace-nowrap">
											{formatShares(pos.shares)}
										</TableCell>

										<TableCell className="py-3.5 text-sm text-muted-foreground whitespace-nowrap">
											{localFormat(pos.price || 0, pos.currency)}
										</TableCell>

										<TableCell className="py-3.5 text-sm font-semibold text-foreground whitespace-nowrap">
											{localFormat((pos.shares || 0) * (pos.price || 0), pos.currency)}
										</TableCell>

										<TableCell
											className={cn(
												"py-3.5 text-sm font-medium whitespace-nowrap",
												displayReturnPct > 0
													? "text-green-600 dark:text-green-400"
													: displayReturnPct < 0
														? "text-red-500 dark:text-red-400"
														: "text-muted-foreground",
											)}
										>
											{(() => {
												const gain = getUnrealizedGain(pos)
												const hasCost = (pos.cost_basis || pos.average_price) && pos.price
												if (!hasCost)
													return displayReturnPct !== 0
														? `${displayReturnPct > 0 ? "+" : ""}${displayReturnPct.toFixed(2)}%`
														: "—"
												return (
													<div>
														<div className="font-semibold">
															{gain >= 0 ? "+" : ""}
															{localFormat(gain, pos.currency)}
														</div>
														<div className="text-xs opacity-80">
															{displayReturnPct >= 0 ? "+" : ""}
															{displayReturnPct.toFixed(2)}%
														</div>
													</div>
												)
											})()}
										</TableCell>

										<TableCell className="py-3.5 text-sm font-medium text-sky-700 dark:text-sky-400 whitespace-nowrap">
											{(pos.dividendYield || 0).toFixed(2)}%
										</TableCell>

										<TableCell
											className={cn(
												"py-3.5 text-sm font-medium whitespace-nowrap",
												(pos.yield_on_cost || 0) > (pos.dividendYield || 0)
													? "text-green-600 dark:text-green-400"
													: "text-muted-foreground",
											)}
										>
											{pos.data_quality_flag === "yoc_implausible" ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
															<AlertTriangle className="h-3.5 w-3.5" />
															<span>—</span>
														</span>
													</TooltipTrigger>
													<TooltipContent>
														Yield on cost is unavailable: data provider returned an implausible value. Re-syncing the ticker should resolve this.
													</TooltipContent>
												</Tooltip>
											) : (pos.yield_on_cost || 0) > 0 ? (
												`${pos.yield_on_cost!.toFixed(2)}%`
											) : (
												"—"
											)}
										</TableCell>

										<TableCell className="py-3.5 text-sm text-muted-foreground whitespace-nowrap">
											{pos.next_dividend_date
												? new Date(pos.next_dividend_date).toLocaleDateString("en-US", {
														month: "short",
														day: "numeric",
													})
												: "—"}
										</TableCell>

										<TableCell className="py-3.5 text-sm">
											<SafetyBadge
												rating={pos.safety_rating}
												score={pos.safety_score}
												size="sm"
											/>
										</TableCell>

										<TableCell
											className={cn(
												"py-3.5 text-sm font-medium whitespace-nowrap",
												growth > 0
													? "text-green-600 dark:text-green-400"
													: growth < 0
														? "text-red-500 dark:text-red-400"
														: "text-muted-foreground",
											)}
										>
											{growth !== 0 ? `${growth > 0 ? "+" : ""}${growth.toFixed(2)}%` : "—"}
										</TableCell>

										<TableCell className="py-3.5 text-sm text-muted-foreground whitespace-nowrap">
											{pos.frequency || "—"}
										</TableCell>

										<TableCell className="py-3.5 text-sm font-semibold pr-3 sm:pr-6 text-sky-900 dark:text-sky-100 whitespace-nowrap">
											{localFormat((pos.shares || 0) * (pos.dividendPerShare || 0), pos.currency)}
										</TableCell>
									</TableRow>
								)
							})
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}
